import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, message, Popconfirm } from "antd";
import Papa from "papaparse";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import CustomPagination from "src/components/reusable/ReusableTable/CustomPagination";
import { CustomIcon } from "src/components/reusable/CustomIcon";
import { useTranslation } from "react-i18next";
import {
  uploadModifiedCustomList,
  getCustomListCSV,
} from "src/services/AudienceListAPIs";
import {
  CSVData,
  LocationState,
  ParseResult,
} from "../../../EngagementInterfaces";

const ModifyFile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const didFetchRef = useRef(false);

  const [csvData, setCsvData] = useState<CSVData[]>([]);
  const [listName, setListName] = useState("");
  const [editableRowIndex, setEditableRowIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isDataScience =
    (location.state as LocationState)?.data_science === true;

  const pageSize = 5;

  useEffect(() => {
    document.body.style.overflowY = "auto";
  }, []);

  useEffect(() => {
    const state = location.state as LocationState;
    if (!state?.listId) {
      message.error(t("audienceOverview.modifyFile.listIdNotProvided"));
      navigate("/account/engagement/audience");
      return;
    }

    setListName(state.listName);
    if (!didFetchRef.current) {
      fetchCSVData(state.listId);
      didFetchRef.current = true;
    }
  }, [location, navigate]);

  const fetchCSVData = async (listId: number) => {
    try {
      setLoading(true);
      const blob = await getCustomListCSV(listId);
      const text = await blob.text();

      Papa.parse(text, {
        header: true,
        complete: (results: ParseResult) => {
          if (results.data && results.data.length > 0) {
            setCsvData(results.data);
          }
        },
      });
    } catch (error) {
      message.error(
        `${t("audienceOverview.modifyFile.fetchCsvErrorPrefix")} ${
          (error as Error).message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (record: CSVData, field: string, value: string) => {
    setCsvData(prevData =>
      prevData.map(item =>
        item === record ? { ...item, [field]: value } : item
      )
    );
  };

  const handleAddRow = () => {
    if (csvData.length === 0) {
      message.error(t("audienceOverview.modifyFile.noDataStructure"));
      return;
    }

    const lastRow = csvData[csvData.length - 1];
    const hasEmptyValues = Object.values(lastRow).some(
      value => !value.trim()
    );

    if (hasEmptyValues) {
      message.error(t("audienceOverview.modifyFile.fillCurrentRowFirst"));
      return;
    }

    const newRow: CSVData = {};
    Object.keys(csvData[0]).forEach(key => {
      newRow[key] = "";
    });

    setCsvData(prevData => [...prevData, newRow]);
    message.success(t("audienceOverview.modifyFile.newRowAdded"));
  };

  const handleDeleteRow = (index: number) => {
    const newData = [...csvData];
    newData.splice(index, 1);
    setCsvData(newData);
    if (editableRowIndex === index) {
      setEditableRowIndex(null);
    }
    message.success(t("audienceOverview.modifyFile.rowDeleted"));
  };

  const handleSave = async () => {
    try {
      const hasEmptyValues = csvData.some(row =>
        Object.values(row).some(value => !value.trim())
      );

      if (hasEmptyValues) {
        message.error(t("audienceOverview.modifyFile.fillOrRemoveEmptyBeforeSave"));
        return;
      }

      setSaving(true);

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv" });
      const file = new File([blob], "modified.csv", { type: "text/csv" });

      const listId = (location.state as LocationState).listId;
      await uploadModifiedCustomList(listId, file);

      message.success(t("audienceOverview.modifyFile.changesSaved"));
      navigate("/account/engagement/audience");
    } catch (error) {
      message.error(
        `${t("audienceOverview.modifyFile.saveErrorPrefix")} ${
          (error as Error).message
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return csvData.slice(start, start + pageSize);
  }, [csvData, currentPage, pageSize]);

  const columns = useMemo(() => {
    if (csvData.length === 0) {
      return [];
    }

    const headers = Object.keys(csvData[0]);

    const tableCols = headers.map(header => ({
      title: header
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase()),
      dataIndex: header,
      key: header,
      render: (_: string, record: CSVData) => {
        const rowIndex = csvData.indexOf(record);
        const isEditing = !isDataScience && editableRowIndex === rowIndex;

        return isEditing ? (
          <StyledTextInput
            value={record[header]}
            onChange={e => handleDataChange(record, header, e.target.value)}
          />
        ) : (
          <span className="modify-file-cell-text">{record[header]}</span>
        );
      },
    }));

    if (!isDataScience) {
      tableCols.push({
        title: t("audienceOverview.modifyFile.actions"),
        key: "actions",
        dataIndex: "actions",
        render: (_: string, record: CSVData) => {
          const rowIndex = csvData.indexOf(record);
          return (
            <div className="modify-file-actions">
              <CustomIcon
                name="edit"
                tooltip={t("audienceOverview.modifyFile.editTooltip")}
                onClick={() => setEditableRowIndex(rowIndex)}
              />
              <Popconfirm
                title={t("audienceOverview.modifyFile.deleteThisRow")}
                onConfirm={() => handleDeleteRow(rowIndex)}
                okText={t("audienceOverview.modifyFile.yes")}
                cancelText={t("audienceOverview.modifyFile.no")}
              >
                <span>
                  <CustomIcon
                    name="delete"
                    tooltip={t("audienceOverview.modifyFile.deleteTooltip")}
                  />
                </span>
              </Popconfirm>
            </div>
          );
        },
      });
    }

    return tableCols;
  }, [csvData, editableRowIndex, isDataScience]);

  const subtitleText = isDataScience
    ? t("audienceOverview.modifyFile.reviewSubtitle")
    : t("audienceOverview.modifyFile.editSubtitle");
  const pageTitle = isDataScience
    ? t("audienceOverview.modifyFile.viewTitle")
    : t("audienceOverview.modifyFile.editTitle");

  if (loading) {
    return (
      <div className="centered-fullscreen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="modify-file-container">
      <button
        className="button button--primary modify-file-back-button"
        onClick={() => navigate("/account/engagement/audience")}
      >
        {t("audienceOverview.modifyFile.back")}
      </button>

      <h1 className="modify-file-title">{pageTitle}</h1>
      <p className="modify-file-subtitle">{subtitleText}</p>

      <div className="modify-file-card">
        <div className="modify-file-card-header">{listName}</div>
        <CampaignTable
          className="modify-file-table"
          columns={columns}
          data={paginatedData}
          rowKey={(_, index) =>
            `${(currentPage - 1) * pageSize + (index ?? 0)}`
          }
          pagination={false}
          customPagination={
            <CustomPagination
              current={currentPage}
              pageSize={pageSize}
              total={csvData.length}
              onChange={setCurrentPage}
              showTotal={false}
            />
          }
        />
      </div>

      {!isDataScience && (
        <div className="action-buttons">
          <Button
            type="primary"
            className="button button--outline"
            onClick={handleAddRow}
          >
            {t("audienceOverview.modifyFile.addNewRow")}
          </Button>
          <Button
            type="primary"
            className="button button--primary"
            onClick={handleSave}
            loading={saving}
          >
            {t("audienceOverview.modifyFile.saveChanges")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ModifyFile;
